import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { LegalEntityCompanyRutMode } from "@/hooks/useNewCertificateRequestFlow";
import type { ConsumerCompanyRegistrationSnapshot, ValidateRutResponse } from "@/services/authService";
import type { ManualCompanyFieldsState } from "@/utils/manualCompanySnapshot";

export type LegalDocumentType = "national_id" | "passport";

export interface LegalEntityRequestFieldsProps {
  /** When `choose`, show registered-company card; `session` uses account RUT without validating; `other` shows Tax ID entry. */
  legalEntityCompanyRutMode: LegalEntityCompanyRutMode;
  /** Set when the account is a registered company with a 12-digit RUT (drives the choose card). */
  registeredCompanySnapshot: ConsumerCompanyRegistrationSnapshot | null;
  onSelectSessionCompany: () => void;
  onSelectOtherCompany: () => void;
  onReturnToCompanyChoice: () => void;
  rut: string;
  onRutChange: (digits: string) => void;
  onValidateRut: () => void;
  isValidatingRut: boolean;
  /** Registry-validated company, if lookup succeeded. */
  validatedCompany: ValidateRutResponse | null;
  /** When true, registry was unavailable; user can enter company data manually. */
  manualMode: boolean;
  manualFields: ManualCompanyFieldsState;
  onManualFieldChange: (field: keyof ManualCompanyFieldsState, value: string) => void;
  /** Effective company for display and for unlocking document uploads (registry or manual). */
  companyPreview: ValidateRutResponse | null;
  estatutoFile: File | null;
  onEstatutoChange: (file: File | null) => void;
  comunicacionBcuFile: File | null;
  onComunicacionBcuChange: (file: File | null) => void;
  legalDocumentType: LegalDocumentType;
  onLegalDocumentTypeChange: (value: LegalDocumentType) => void;
  legalDocumentFrontFile: File | null;
  onLegalDocumentFrontChange: (file: File | null) => void;
  legalDocumentBackFile: File | null;
  onLegalDocumentBackChange: (file: File | null) => void;
  passportDocumentFile: File | null;
  onPassportDocumentChange: (file: File | null) => void;
}

export function LegalEntityRequestFields({
  legalEntityCompanyRutMode,
  registeredCompanySnapshot,
  onSelectSessionCompany,
  onSelectOtherCompany,
  onReturnToCompanyChoice,
  rut,
  onRutChange,
  onValidateRut,
  isValidatingRut,
  validatedCompany,
  manualMode,
  manualFields,
  onManualFieldChange,
  companyPreview,
  estatutoFile,
  onEstatutoChange,
  comunicacionBcuFile,
  onComunicacionBcuChange,
  legalDocumentType,
  onLegalDocumentTypeChange,
  legalDocumentFrontFile,
  onLegalDocumentFrontChange,
  legalDocumentBackFile,
  onLegalDocumentBackChange,
  passportDocumentFile,
  onPassportDocumentChange,
}: LegalEntityRequestFieldsProps): JSX.Element {
  const { t } = useTranslation();

  const showRutEntry = legalEntityCompanyRutMode === "other";
  const chooseCard =
    legalEntityCompanyRutMode === "choose" && registeredCompanySnapshot != null ? (
      <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20">
        <CardHeader className="space-y-2 pb-2">
          <CardTitle className="text-lg font-semibold leading-snug tracking-tight text-foreground">
            {t("requests.user.legalEntityRegisteredCompanyTitle")}
          </CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            {t("requests.user.legalEntityRegisteredCompanyHint")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border/70 bg-muted/15 p-4 dark:bg-secondary/20">
            <p className="text-sm font-semibold text-foreground">
              {registeredCompanySnapshot.legalName}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("requests.user.legalEntityRegisteredCompanyRutLabel")}:{" "}
              <span className="font-mono text-foreground">
                {registeredCompanySnapshot.rut.replace(/\D/g, "").slice(0, 12)}
              </span>
            </p>
            {registeredCompanySnapshot.legalAddress ? (
              <p className="mt-2 text-sm text-muted-foreground">{registeredCompanySnapshot.legalAddress}</p>
            ) : null}
            {registeredCompanySnapshot.city ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {[registeredCompanySnapshot.city, registeredCompanySnapshot.department]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-3">
            <Button
              type="button"
              variant="outline"
              className="transition-colors duration-150"
              onClick={() => onSelectOtherCompany()}
            >
              {t("requests.user.legalEntityUseOtherCompanyButton")}
            </Button>
            <Button
              type="button"
              className="transition-colors duration-150"
              onClick={() => onSelectSessionCompany()}
            >
              {t("requests.user.legalEntityUseRegisteredCompanyButton")}
            </Button>
          </div>
        </CardContent>
      </Card>
    ) : null;

  return (
    <div className="space-y-6">
      {chooseCard}

      {showRutEntry && registeredCompanySnapshot != null ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="link"
            className="h-auto min-h-10 px-0 py-1 text-muted-foreground transition-colors duration-150 hover:text-foreground"
            onClick={() => onReturnToCompanyChoice()}
          >
            {t("requests.user.legalEntityBackToRegisteredChoiceLink")}
          </Button>
        </div>
      ) : null}

      {showRutEntry ? (
        <div className="space-y-2">
          <Label htmlFor="legal-entity-rut" className="text-sm font-medium text-foreground">
            {t("requests.user.legalEntityRutLabel")}
          </Label>
          <div className="flex gap-2">
            <Input
              id="legal-entity-rut"
              value={rut}
              onChange={(e) => {
                onRutChange(e.target.value.replace(/\D/g, "").slice(0, 12));
              }}
              placeholder={t("requests.user.legalEntityRutPlaceholder")}
              inputMode="numeric"
              maxLength={12}
              className="flex-1 bg-card"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void onValidateRut()}
              disabled={isValidatingRut}
              className="transition-colors duration-150"
            >
              {isValidatingRut ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  {t("requests.user.validatingRut")}
                </>
              ) : (
                t("requests.user.validateRutButton")
              )}
            </Button>
          </div>
        </div>
      ) : null}

      {showRutEntry && manualMode && validatedCompany == null ? (
        <div className="space-y-3 rounded-xl border border-border/80 bg-muted/20 p-4 dark:border-border/80 dark:bg-secondary/25">
          <p className="text-sm text-muted-foreground">{t("requests.user.manualCompanyIntro")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="le-manual-legal-name" className="text-sm font-medium text-foreground">
                {t("requests.user.manualCompanyLegalName")}
              </Label>
              <Input
                id="le-manual-legal-name"
                value={manualFields.legalName}
                onChange={(e) => onManualFieldChange("legalName", e.target.value)}
                className="bg-card"
                autoComplete="organization"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="le-manual-address" className="text-sm font-medium text-foreground">
                {t("requests.user.manualCompanyAddress")}
              </Label>
              <Input
                id="le-manual-address"
                value={manualFields.legalAddress}
                onChange={(e) => onManualFieldChange("legalAddress", e.target.value)}
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="le-manual-city" className="text-sm font-medium text-foreground">
                {t("requests.user.manualCompanyCity")}
              </Label>
              <Input
                id="le-manual-city"
                value={manualFields.city}
                onChange={(e) => onManualFieldChange("city", e.target.value)}
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="le-manual-dept" className="text-sm font-medium text-foreground">
                {t("requests.user.manualCompanyDepartment")}
              </Label>
              <Input
                id="le-manual-dept"
                value={manualFields.department}
                onChange={(e) => onManualFieldChange("department", e.target.value)}
                className="bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="le-manual-phone" className="text-sm font-medium text-foreground">
                {t("requests.user.manualCompanyPhone")}
              </Label>
              <Input
                id="le-manual-phone"
                value={manualFields.phone}
                onChange={(e) => onManualFieldChange("phone", e.target.value)}
                className="bg-card"
                inputMode="tel"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="le-manual-email" className="text-sm font-medium text-foreground">
                {t("requests.user.manualCompanyEmail")}
              </Label>
              <Input
                id="le-manual-email"
                value={manualFields.email}
                onChange={(e) => onManualFieldChange("email", e.target.value)}
                className="bg-card"
                type="email"
                autoComplete="email"
              />
            </div>
          </div>
        </div>
      ) : null}

      {companyPreview ? (
        <div className="wizard-summary-panel p-4">
          <p className="text-sm font-semibold text-foreground">{companyPreview.legalName}</p>
          {companyPreview.legalAddress ? (
            <p className="mt-1 text-sm text-muted-foreground">{companyPreview.legalAddress}</p>
          ) : null}
        </div>
      ) : null}

      {companyPreview != null && legalEntityCompanyRutMode === "session" ? (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="link"
            className="h-auto min-h-10 px-0 py-1 text-muted-foreground transition-colors duration-150 hover:text-foreground"
            onClick={() => onSelectOtherCompany()}
          >
            {t("requests.user.legalEntityDifferentCompanyLink")}
          </Button>
        </div>
      ) : null}

      {companyPreview ? (
        <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20">
          <CardHeader className="space-y-2 pb-2">
            <CardTitle className="text-lg font-semibold leading-snug tracking-tight text-foreground">
              {t("requests.user.legalEntityRequiredDocs")}
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              {t("requests.user.legalEntityRequiredDocsHint")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8 pt-2">
            <div className="space-y-5">
              <StyledFileInput
                id="legal-entity-estatuto"
                accept=".pdf,image/*"
                value={estatutoFile}
                onChange={onEstatutoChange}
                label={t("requests.user.requestTypes.document_estatuto")}
                hint={t("requests.user.legalEntityFileHint")}
              />
              <StyledFileInput
                id="legal-entity-comunicacion-bcu"
                accept=".pdf,image/*"
                value={comunicacionBcuFile}
                onChange={onComunicacionBcuChange}
                label={t("requests.user.requestTypes.document_comunicacion_bcu")}
                hint={t("requests.user.legalEntityFileHint")}
              />
            </div>

            <Separator className="bg-border/80" />

            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-foreground">
                  {t("requests.user.legalEntityIdentitySectionTitle")}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("requests.user.legalEntityIdentitySectionHint")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="legal-entity-doc-type" className="text-sm font-medium text-muted-foreground">
                  {t("requests.user.legalDocumentTypeLabel")}
                </Label>
                <Select
                  value={legalDocumentType}
                  onValueChange={(value) => onLegalDocumentTypeChange(value as LegalDocumentType)}
                >
                  <SelectTrigger
                    id="legal-entity-doc-type"
                    className="w-full max-w-md bg-card transition-colors duration-150"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="national_id" className="cursor-pointer">
                      {t("requests.user.legalDocumentNationalId")}
                    </SelectItem>
                    <SelectItem value="passport" className="cursor-pointer">
                      {t("requests.user.legalDocumentPassport")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {legalDocumentType === "national_id" ? (
                <div className="space-y-5 border-t border-border/70 pt-5">
                  <StyledFileInput
                    id="legal-entity-legal-document-front"
                    accept=".pdf,image/*"
                    value={legalDocumentFrontFile}
                    onChange={onLegalDocumentFrontChange}
                    label={t("requests.user.requestTypes.document_legal_document_front")}
                    hint={t("requests.user.legalEntityFileHint")}
                  />
                  <StyledFileInput
                    id="legal-entity-legal-document-back"
                    accept=".pdf,image/*"
                    value={legalDocumentBackFile}
                    onChange={onLegalDocumentBackChange}
                    label={t("requests.user.requestTypes.document_legal_document_back")}
                    hint={t("requests.user.legalEntityFileHint")}
                  />
                </div>
              ) : (
                <div className="border-t border-border/70 pt-5">
                  <StyledFileInput
                    id="legal-entity-passport-document"
                    accept=".pdf,image/*"
                    value={passportDocumentFile}
                    onChange={onPassportDocumentChange}
                    label={t("requests.user.requestTypes.document_passport_document")}
                    hint={t("requests.user.legalEntityFileHint")}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
